/**
 * MealsTab — Onglet Repas du module Crèche.
 *
 * Sections :
 *  1. Sélecteur année scolaire + KPIs cards
 *  2. Graphes (évolution mensuelle, répartition Midi/Goûter, par gamme d'âge)
 *  3. Upload facture PDF (preview + confirm)
 *  4. Liste factures (téléchargement, suppression)
 *  5. Alertes prix
 */
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
  Upload, Download, Trash2, TrendingUp, TrendingDown,
  ChefHat, Cookie, Sandwich, AlertTriangle, X, FileText, Loader2,
  BarChart3, PieChart as PieIcon, ChevronLeft, ChevronRight,
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, Legend, CartesianGrid,
} from 'recharts';
import {
  getMealsDashboard, getMealsComparison, getMealInvoices, deleteMealInvoice,
  previewMealInvoice, confirmMealInvoice, downloadMealInvoice,
} from '../../api';
import { useStructure } from '../../contexts/StructureContext';
import { useToast } from '../../contexts/NotificationContext';
import LoadingSpinner from '../LoadingSpinner';

const AGE_LABELS = {
  diversification: 'Diversification (bébés)',
  petit_muscle: 'Petit musclé (moyens)',
  petit_croco: 'Petit croco (grands)',
  roi_lion: 'Roi lion (grands)',
  robe_champs: 'Robe des champs',
};

const AGE_COLORS = {
  diversification: '#f59e0b',
  petit_muscle: '#10b981',
  petit_croco: '#8b5cf6',
  roi_lion: '#ef4444',
  robe_champs: '#3b82f6',
};

const fmtEur = (n) => (n ?? 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const fmtEurP = (n) => (n ?? 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (s) => s ? new Date(s).toLocaleDateString('fr-FR') : '—';
const fmtMonth = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  return d.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' });
};

export default function MealsTab({ showAllStructures = false }) {
  const { currentStructure, structures } = useStructure();
  const toast = useToast();

  const now = new Date();
  const defaultSchoolYear = now.getMonth() >= 8 ? now.getFullYear() + 1 : now.getFullYear();
  const [schoolYear, setSchoolYear] = useState(defaultSchoolYear);
  const [dashboard, setDashboard] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadModal, setUploadModal] = useState(false);
  const [detailInvoice, setDetailInvoice] = useState(null);

  const structureIds = showAllStructures ? structures.map(s => s.id).join(',') : null;
  const activeStructureId = showAllStructures ? undefined : currentStructure?.id;

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, compRes, invRes] = await Promise.all([
        getMealsDashboard({ schoolYear, structureId: activeStructureId, structureIds }),
        getMealsComparison({ schoolYear, structureId: activeStructureId }),
        getMealInvoices({ structureId: activeStructureId, structureIds }),
      ]);
      setDashboard(dashRes.data);
      setComparison(compRes.data);
      setInvoices(invRes.data);
    } catch (e) {
      toast.error('Erreur chargement Repas');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolYear, activeStructureId, structureIds]);

  useEffect(() => { loadAll(); }, [loadAll]);

  if (loading) {
    return <div className="p-8 flex justify-center"><LoadingSpinner size="large" text="Chargement des repas..." /></div>;
  }

  const k = dashboard?.kpis || {};
  const deltaCost = comparison?.current?.ttc && comparison?.previous?.ttc
    ? ((comparison.current.ttc - comparison.previous.ttc) / comparison.previous.ttc) * 100
    : null;

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* En-tête + sélecteur année scolaire */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <ChefHat size={20} className="text-amber-600" />
            Suivi des repas — GOÛT'CHOU
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Analyse des factures fournisseur, coûts et consommation par gamme d'âge
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSchoolYear(y => y - 1)}
            className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
            title="Année scolaire précédente"
          ><ChevronLeft size={16} /></button>
          <div className="px-4 py-1.5 bg-amber-50 text-amber-700 rounded-lg text-sm font-semibold border border-amber-200">
            Année scolaire {schoolYear - 1}/{String(schoolYear).slice(2)}
          </div>
          <button
            onClick={() => setSchoolYear(y => y + 1)}
            className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
            title="Année scolaire suivante"
          ><ChevronRight size={16} /></button>
          <button
            onClick={() => setUploadModal(true)}
            className="ml-2 px-3 py-1.5 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 inline-flex items-center gap-1.5"
          >
            <Upload size={14} /> Importer une facture
          </button>
        </div>
      </div>

      {/* KPIs cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard icon={<TrendingUp size={16} />} label="Coût total TTC" value={fmtEur(k.totalTTC)} sub={dashboard.invoicesCount + ' factures'} color="amber" />
        <KpiCard icon={<Sandwich size={16} />} label="Repas servis" value={(k.totalMeals || 0).toLocaleString('fr-FR')} sub="midi + goûter" color="emerald" />
        <KpiCard icon={<TrendingUp size={16} />} label="Coût moyen" value={fmtEurP(k.avgCostPerMeal)} sub="/ repas" color="indigo" />
        <KpiCard icon={<ChefHat size={16} />} label="Midi" value={(k.totalMidi || 0).toLocaleString('fr-FR')} sub={k.totalMeals ? `${((k.totalMidi / k.totalMeals) * 100).toFixed(0)}%` : ''} color="rose" />
        <KpiCard icon={<Cookie size={16} />} label="Goûters" value={(k.totalGouter || 0).toLocaleString('fr-FR')} sub={k.totalMeals ? `${((k.totalGouter / k.totalMeals) * 100).toFixed(0)}%` : ''} color="orange" />
        <KpiCard icon={<AlertTriangle size={16} />} label="Alternatifs" value={(k.totalAlternate || 0).toLocaleString('fr-FR')} sub={`${(k.alternateRatio || 0).toFixed(1)}%`} color="violet" />
      </div>

      {/* Comparaison N vs N-1 */}
      {comparison?.previous?.invoices > 0 && (
        <div className="bg-white border rounded-xl p-4">
          <div className="text-sm font-semibold text-gray-700 mb-2">Comparaison avec l'année précédente</div>
          <div className="grid grid-cols-3 gap-4">
            <ComparisonBox label={`École ${schoolYear - 1}/${String(schoolYear).slice(2)}`} value={fmtEur(comparison.current.ttc)} meta={`${comparison.current.meals} repas`} />
            <ComparisonBox label={`École ${schoolYear - 2}/${String(schoolYear - 1).slice(2)}`} value={fmtEur(comparison.previous.ttc)} meta={`${comparison.previous.meals} repas`} />
            <div className={`rounded-lg p-3 ${deltaCost !== null && deltaCost > 0 ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>
              <div className="text-xs opacity-80">Évolution</div>
              <div className="text-lg font-bold flex items-center gap-1">
                {deltaCost !== null && (deltaCost > 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />)}
                {deltaCost !== null ? `${deltaCost > 0 ? '+' : ''}${deltaCost.toFixed(1)}%` : '—'}
              </div>
              <div className="text-xs opacity-70 mt-1">vs N-1</div>
            </div>
          </div>
        </div>
      )}

      {/* Graphes */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Évolution mensuelle */}
        <div className="bg-white border rounded-xl p-4 lg:col-span-2">
          <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-gray-700">
            <BarChart3 size={14} /> Évolution mensuelle
          </div>
          {dashboard?.monthly?.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={dashboard.monthly}>
                <defs>
                  <linearGradient id="ttc" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#d97706" stopOpacity={0.7} />
                    <stop offset="95%" stopColor="#d97706" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip
                  formatter={(v, name) => name === 'ttc' ? [fmtEurP(v), 'Coût TTC'] : [v, 'Repas']}
                />
                <Area type="monotone" dataKey="ttc" stroke="#d97706" fill="url(#ttc)" name="ttc" />
              </AreaChart>
            </ResponsiveContainer>
          ) : <EmptyChart />}
        </div>

        {/* Répartition par gamme d'âge */}
        <div className="bg-white border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-gray-700">
            <PieIcon size={14} /> Répartition par gamme d'âge
          </div>
          {dashboard?.byAgeRange?.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={dashboard.byAgeRange}
                  dataKey="meals"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={70}
                  label={(e) => `${((e.percent || 0) * 100).toFixed(0)}%`}
                >
                  {dashboard.byAgeRange.map((e, i) => (
                    <Cell key={i} fill={AGE_COLORS[e.name] || '#94a3b8'} />
                  ))}
                </Pie>
                <Tooltip formatter={(v, _n, p) => [`${v} repas`, AGE_LABELS[p.payload.name] || p.payload.name]} />
                <Legend
                  formatter={(v) => <span style={{ fontSize: 11 }}>{AGE_LABELS[v] || v}</span>}
                  iconSize={8}
                  wrapperStyle={{ fontSize: 11 }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : <EmptyChart />}
        </div>

        {/* Repas par mois — Midi vs Goûter */}
        <div className="bg-white border rounded-xl p-4 lg:col-span-2">
          <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-gray-700">
            <BarChart3 size={14} /> Nombre de repas par mois
          </div>
          {dashboard?.monthly?.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={dashboard.monthly}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="midi" stackId="a" fill="#ef4444" name="Midi" />
                <Bar dataKey="gouter" stackId="a" fill="#f59e0b" name="Goûter" />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyChart />}
        </div>

        {/* Répartition par structure */}
        <div className="bg-white border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-gray-700">
            <PieIcon size={14} /> Par structure
          </div>
          {dashboard?.byStructure?.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={dashboard.byStructure}
                  dataKey="ttc"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={70}
                  label={(e) => e.name}
                >
                  {dashboard.byStructure.map((e, i) => (
                    <Cell key={i} fill={['#0d9488', '#7c3aed', '#f59e0b'][i % 3]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => [fmtEurP(v), 'Coût TTC']} />
              </PieChart>
            </ResponsiveContainer>
          ) : <EmptyChart />}
        </div>
      </div>

      {/* Alertes prix */}
      {dashboard?.priceAlerts?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-amber-800">
            <AlertTriangle size={14} /> Variations de prix ({dashboard.priceAlerts.length})
          </div>
          <div className="space-y-1.5">
            {dashboard.priceAlerts.map((a, i) => (
              <div key={i} className="flex items-center justify-between text-xs bg-white rounded p-2 border border-amber-100">
                <span className="font-medium text-gray-800">{a.product}</span>
                <span className="font-mono">
                  {fmtEurP(a.firstPrice)} → {fmtEurP(a.lastPrice)}
                  <span className={`ml-2 font-bold ${a.pct > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                    ({a.pct > 0 ? '+' : ''}{a.pct.toFixed(1)}%)
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Liste factures */}
      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <div className="text-sm font-semibold text-gray-700">
            Historique des factures ({invoices.length})
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-xs uppercase text-gray-500">
                <th className="px-3 py-2 text-left">Période</th>
                <th className="px-3 py-2 text-left">Référence</th>
                <th className="px-3 py-2 text-left">Structure</th>
                <th className="px-3 py-2 text-right">Repas</th>
                <th className="px-3 py-2 text-right">TTC</th>
                <th className="px-3 py-2 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-gray-400 text-sm">
                    Aucune facture importée. Cliquez sur « Importer une facture ».
                  </td>
                </tr>
              ) : invoices.map((inv) => (
                <tr key={inv.id} className="border-t hover:bg-gray-50">
                  <td className="px-3 py-2 text-gray-800">
                    {fmtMonth(inv.periodStart)}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-gray-600">{inv.invoiceRef || '—'}</td>
                  <td className="px-3 py-2 text-gray-700">{inv.structure?.name || '—'}</td>
                  <td className="px-3 py-2 text-right text-gray-600">{inv._count?.lines || 0}</td>
                  <td className="px-3 py-2 text-right font-semibold text-gray-800">{fmtEurP(inv.totalTTC)}</td>
                  <td className="px-3 py-2 text-center">
                    <div className="inline-flex gap-1">
                      <button onClick={() => setDetailInvoice(inv)} className="p-1 hover:bg-gray-100 rounded text-gray-500" title="Voir détail"><FileText size={14} /></button>
                      <button onClick={async () => {
                        try {
                          const res = await downloadMealInvoice(inv.id);
                          const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                          window.open(url, '_blank');
                        } catch { toast.error('Erreur téléchargement'); }
                      }} className="p-1 hover:bg-gray-100 rounded text-gray-500" title="Télécharger PDF"><Download size={14} /></button>
                      <button onClick={async () => {
                        if (!window.confirm(`Supprimer la facture ${inv.invoiceRef || '(sans ref)'} ?`)) return;
                        try {
                          await deleteMealInvoice(inv.id);
                          toast.success('Facture supprimée');
                          loadAll();
                        } catch { toast.error('Erreur suppression'); }
                      }} className="p-1 hover:bg-red-100 rounded text-red-500" title="Supprimer"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modales */}
      {uploadModal && (
        <UploadModal
          structures={structures}
          currentStructureId={currentStructure?.id}
          onClose={() => setUploadModal(false)}
          onDone={() => { setUploadModal(false); loadAll(); }}
        />
      )}
      {detailInvoice && (
        <InvoiceDetailModal
          invoiceId={detailInvoice.id}
          onClose={() => setDetailInvoice(null)}
        />
      )}
    </div>
  );
}

// ─── Sous-composants ──────────────────────────────────────────────────

function KpiCard({ icon, label, value, sub, color = 'gray' }) {
  const colorMap = {
    amber:   'bg-amber-50 text-amber-700 border-amber-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    indigo:  'bg-indigo-50 text-indigo-700 border-indigo-200',
    rose:    'bg-rose-50 text-rose-700 border-rose-200',
    orange:  'bg-orange-50 text-orange-700 border-orange-200',
    violet:  'bg-violet-50 text-violet-700 border-violet-200',
    gray:    'bg-gray-50 text-gray-700 border-gray-200',
  };
  return (
    <div className={`rounded-xl border p-3 ${colorMap[color]}`}>
      <div className="flex items-center gap-1.5 text-[11px] font-semibold opacity-80 uppercase tracking-wide">
        {icon} {label}
      </div>
      <div className="text-xl font-bold mt-1">{value}</div>
      {sub && <div className="text-[11px] opacity-70 mt-0.5">{sub}</div>}
    </div>
  );
}

function ComparisonBox({ label, value, meta }) {
  return (
    <div className="rounded-lg p-3 bg-gray-50 border border-gray-200">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-lg font-bold text-gray-800">{value}</div>
      <div className="text-xs text-gray-500 mt-1">{meta}</div>
    </div>
  );
}

function EmptyChart() {
  return (
    <div className="h-[240px] flex items-center justify-center text-gray-400 text-sm">
      Aucune donnée
    </div>
  );
}

function UploadModal({ structures, currentStructureId, onClose, onDone }) {
  const toast = useToast();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [structureId, setStructureId] = useState(currentStructureId || '');
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const inputRef = useRef(null);

  const handleFileSelected = async (f) => {
    if (!f) return;
    if (f.type !== 'application/pdf') { toast.error('PDF uniquement'); return; }
    setFile(f);
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await previewMealInvoice(fd);
      setPreview(res.data);
      if (res.data.suggestedStructureId) setStructureId(res.data.suggestedStructureId);
      if (res.data.alreadyExists) toast.warning('Cette facture est déjà en base');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur lecture PDF');
      setFile(null);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!preview || !structureId) { toast.error('Structure requise'); return; }
    setConfirming(true);
    try {
      await confirmMealInvoice({ previewId: preview.previewId, structureId });
      toast.success('Facture importée');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur import');
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Upload size={18} className="text-amber-600" /> Importer une facture GOÛT'CHOU
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="flex-1 overflow-auto p-5 space-y-4">
          {!preview ? (
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-10 text-center">
              <input type="file" accept="application/pdf" ref={inputRef} onChange={(e) => handleFileSelected(e.target.files?.[0])} className="hidden" />
              <button onClick={() => inputRef.current?.click()} className="text-amber-600 font-medium hover:underline">
                Cliquer pour sélectionner un PDF
              </button>
              <p className="text-xs text-gray-500 mt-2">ou glisser-déposer</p>
              {loading && <div className="mt-4 flex items-center justify-center gap-2 text-sm text-gray-500"><Loader2 size={16} className="animate-spin" /> Analyse en cours...</div>}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Field label="Référence" value={preview.parsed.invoiceRef || '(draft)'} />
                <Field label="Date" value={fmtDate(preview.parsed.invoiceDate)} />
                <Field label="Période" value={`${fmtDate(preview.parsed.periodStart)} → ${fmtDate(preview.parsed.periodEnd)}`} />
                <Field label="Code client" value={preview.parsed.clientCode || '—'} />
                <Field label="Total HT" value={fmtEurP(preview.parsed.totalHT)} />
                <Field label="Total TTC" value={fmtEurP(preview.parsed.totalTTC)} bold />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Structure (destination)</label>
                <select value={structureId} onChange={(e) => setStructureId(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm">
                  <option value="">— Sélectionner —</option>
                  {structures.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                {preview.suggestedStructureId && (
                  <p className="text-xs text-emerald-600 mt-1">Structure suggérée depuis le code client</p>
                )}
              </div>

              {preview.alreadyExists && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                  ⚠ Cette facture (référence {preview.parsed.invoiceRef}) est déjà présente en base. L'import sera refusé.
                </div>
              )}

              <div>
                <div className="text-xs font-semibold text-gray-600 mb-2">Lignes détectées ({preview.parsed.linesCount})</div>
                <div className="max-h-60 overflow-auto border rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="text-left px-2 py-1">Produit</th>
                        <th className="text-left px-2 py-1">Catégorie</th>
                        <th className="text-right px-2 py-1">Qté</th>
                        <th className="text-right px-2 py-1">PU HT</th>
                        <th className="text-right px-2 py-1">Total HT</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.parsed.lines.map((l, i) => (
                        <tr key={i} className="border-t">
                          <td className="px-2 py-1">
                            {l.productName}
                            {l.isAlternate && <span className="ml-1 text-[10px] bg-violet-100 text-violet-700 px-1 rounded">alt</span>}
                          </td>
                          <td className="px-2 py-1 text-gray-500">{l.category || '?'}</td>
                          <td className="px-2 py-1 text-right">{l.quantity}</td>
                          <td className="px-2 py-1 text-right font-mono">{Number(l.unitPriceHT).toFixed(2)}</td>
                          <td className="px-2 py-1 text-right font-mono">{Number(l.totalHT).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
        <div className="px-5 py-3 border-t bg-gray-50 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Annuler</button>
          {preview && (
            <button
              onClick={handleConfirm}
              disabled={confirming || preview.alreadyExists || !structureId}
              className="px-4 py-2 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              {confirming ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              Confirmer l'import
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function InvoiceDetailModal({ invoiceId, onClose }) {
  const [invoice, setInvoice] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { getMealInvoiceById } = await import('../../api');
        const res = await getMealInvoiceById(invoiceId);
        setInvoice(res.data);
      } catch {}
    })();
  }, [invoiceId]);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold">
            Détail facture {invoice?.invoiceRef || ''}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="flex-1 overflow-auto p-5">
          {!invoice ? (
            <div className="text-center py-10 text-gray-400"><Loader2 size={20} className="animate-spin inline" /></div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                <Field label="Période" value={`${fmtDate(invoice.periodStart)} → ${fmtDate(invoice.periodEnd)}`} />
                <Field label="Structure" value={invoice.structure?.name || '—'} />
                <Field label="Total HT" value={fmtEurP(invoice.totalHT)} />
                <Field label="Total TTC" value={fmtEurP(invoice.totalTTC)} bold />
              </div>
              <table className="w-full text-sm border rounded-lg overflow-hidden">
                <thead>
                  <tr className="bg-gray-50 text-xs uppercase text-gray-500">
                    <th className="text-left px-2 py-2">Produit</th>
                    <th className="text-left px-2 py-2">Catégorie</th>
                    <th className="text-right px-2 py-2">Qté</th>
                    <th className="text-right px-2 py-2">PU HT</th>
                    <th className="text-right px-2 py-2">Total HT</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.lines.map((l) => (
                    <tr key={l.id} className="border-t">
                      <td className="px-2 py-1.5">
                        {l.productName}
                        {l.isAlternate && <span className="ml-1 text-[10px] bg-violet-100 text-violet-700 px-1 rounded">alt</span>}
                      </td>
                      <td className="px-2 py-1.5 text-gray-500">{l.category || '?'}</td>
                      <td className="px-2 py-1.5 text-right">{l.quantity}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{Number(l.unitPriceHT).toFixed(2)}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{Number(l.totalHT).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, bold }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-gray-400 font-semibold tracking-wide">{label}</div>
      <div className={`text-gray-800 ${bold ? 'font-bold' : ''}`}>{value}</div>
    </div>
  );
}
